/**
 * Route-level view guard. DRA's page count is small enough that a plain
 * "No access" message is simpler and clearer than reporting_app's redirect
 * chain (ProtectedRoute + SmartRedirect) — there's nowhere useful to bounce
 * a Viewer who typed /approvals by hand except back to what they can see.
 */
import { ReactNode } from 'react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { SkeletonBar, LoadingAnnouncement } from './ui/Skeleton';

export default function RequirePage({ page, children }: { page: string; children: ReactNode }) {
  const { canView, isLoading } = usePagePermissions(page);

  if (isLoading) {
    /*
     * P38, task 5. The shell is already on screen here — this is inside `<main>` —
     * so what is missing is the page, and the honest shape of a page that has not
     * arrived is a heading and a body. The word `Loading…` in the top-left corner
     * of a full-width panel read as an error message.
     *
     * No skeleton TABLE, because this guard does not know whether the page behind
     * it is a table, a matrix or a list of cards. The page's own loading state does
     * (see `TicketTable`'s `isLoading`); promising rows here and then rendering
     * `/settings` would be the shift the skeleton exists to prevent.
     */
    return (
      <div className="space-y-4">
        <LoadingAnnouncement what="this page" />
        <SkeletonBar className="h-5 w-48" />
        <SkeletonBar className="w-2/3" />
        <SkeletonBar className="w-1/2" />
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-ink">No access</h1>
        <p className="mt-1 text-sm text-gray-500">You don't have permission to view this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}
